/**
 * The drafting-paper grid the 3D view draws behind its scene, at either
 * quality: one triangle covering the screen, whose fragment shader shades
 * each pixel by its distance to the nearest minor and major line, spaced and
 * anchored as `View3DCore` lays the grid out (`GridPlacement`). Lines stay
 * `GRID_LINE_WIDTH` wide on screen and antialiased at any zoom, and a frame
 * costs one draw whatever the spacing, with no line geometry and nothing
 * uploaded or allocated per frame. The paper and the line colors are
 * `PALETTE`'s, the ones the 2D plan grid draws with; as in the plan views'
 * 3D grid until now, a major line is drawn over the minor one beneath it.
 *
 * Each renderer draws it on the screen right after clearing it, with depth
 * writes off, so the scene's pixels cover it and the background shows it.
 * Plain WebGL1, so it draws on any context.
 */

import { GRID_LINE_WIDTH, PALETTE } from '../editor-model';
import { linkProgram } from './glRenderer';
import type { GridPlacement } from './View3DCore';

/** The covering triangle's corners, in clip space: past the screen's edges, so it covers every pixel. */
const COVERING_TRIANGLE = new Float32Array([-1, -1, 3, -1, -1, 3]);

const VERTEX = `
attribute vec2 aCorner;
void main() {
  gl_Position = vec4(aCorner, 0.0, 1.0);
}`;

/** High precision where the device has it: a pixel's position across a big screen needs more than half floats hold. */
const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uAnchor;
uniform float uMinorSpacing;
uniform float uMajorSpacing;
uniform float uLineWidth;
uniform vec3 uBackground;
uniform vec3 uLineColor;
uniform float uMinorOpacity;
uniform float uMajorOpacity;
float lineCoverage(vec2 position, float spacing) {
  vec2 offLine = abs(mod(position - uAnchor + 0.5 * spacing, spacing) - 0.5 * spacing);
  vec2 coverage = clamp(0.5 * uLineWidth + 0.5 - offLine, 0.0, 1.0);
  return max(coverage.x, coverage.y);
}
void main() {
  vec3 color = mix(uBackground, uLineColor, uMinorOpacity * lineCoverage(gl_FragCoord.xy, uMinorSpacing));
  gl_FragColor = vec4(mix(color, uLineColor, uMajorOpacity * lineCoverage(gl_FragCoord.xy, uMajorSpacing)), 1.0);
}`;

export class GridRenderer {
  private gl: WebGLRenderingContext;
  private program: WebGLProgram;
  private corners: WebGLBuffer;
  private aCorner: number;
  private uAnchor: WebGLUniformLocation | null;
  private uMinorSpacing: WebGLUniformLocation | null;
  private uMajorSpacing: WebGLUniformLocation | null;
  private uLineWidth: WebGLUniformLocation | null;

  /** Compiles the grid's program and uploads its triangle and colors, once. */
  constructor(gl: WebGLRenderingContext) {
    this.gl = gl;
    this.program = linkProgram(gl, VERTEX, FRAGMENT);
    this.aCorner = gl.getAttribLocation(this.program, 'aCorner');
    this.uAnchor = gl.getUniformLocation(this.program, 'uAnchor');
    this.uMinorSpacing = gl.getUniformLocation(this.program, 'uMinorSpacing');
    this.uMajorSpacing = gl.getUniformLocation(this.program, 'uMajorSpacing');
    this.uLineWidth = gl.getUniformLocation(this.program, 'uLineWidth');
    this.corners = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.corners);
    gl.bufferData(gl.ARRAY_BUFFER, COVERING_TRIANGLE, gl.STATIC_DRAW);
    const [lineRed, lineGreen, lineBlue, minorOpacity] = PALETTE.gridMinor;
    gl.useProgram(this.program);
    gl.uniform3f(gl.getUniformLocation(this.program, 'uBackground'), ...PALETTE.stage);
    gl.uniform3f(gl.getUniformLocation(this.program, 'uLineColor'), lineRed, lineGreen, lineBlue);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uMinorOpacity'), minorOpacity);
    gl.uniform1f(gl.getUniformLocation(this.program, 'uMajorOpacity'), PALETTE.gridMajor[3]);
  }

  /**
   * Draw the grid over the whole screen, a surface `devicePixelHeight` tall
   * at `density` device pixels per view pixel (the units `grid` is in), and
   * leave depth testing and writes on for the scene. Each line covers the
   * pixels that start at its place and run its width right or down, as the
   * web's grid always drew it, so a line on whole pixels draws crisp: its
   * center stands half its width on. GL counts window rows from the bottom,
   * so the anchor turns over.
   */
  draw(grid: GridPlacement, density: number, devicePixelHeight: number): void {
    const gl = this.gl;
    const lineWidth = GRID_LINE_WIDTH * density;
    gl.useProgram(this.program);
    gl.uniform2f(this.uAnchor, grid.anchorX * density + lineWidth / 2, devicePixelHeight - grid.anchorY * density - lineWidth / 2);
    gl.uniform1f(this.uMinorSpacing, grid.minorPixels * density);
    gl.uniform1f(this.uMajorSpacing, grid.majorPixels * density);
    gl.uniform1f(this.uLineWidth, lineWidth);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.BLEND);
    gl.depthMask(false);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.corners);
    gl.enableVertexAttribArray(this.aCorner);
    gl.vertexAttribPointer(this.aCorner, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disableVertexAttribArray(this.aCorner);
    gl.depthMask(true);
    gl.enable(gl.DEPTH_TEST);
  }
}
